import { useSelector } from "react-redux";
import { useMemo } from "react";
import { RootState } from "../store/store";
import {
  deriveRole,
  hasPermission,
  Permission,
  Role,
} from "../lib/permissions";

export interface UsePermissionsResult {
  /** Flat permissions array hydrated from the Zitadel JWT / DB role mapping. */
  permissions: string[];
  /** Role derived from the granted permissions (Admin > Member > Viewer). */
  role: Role;
  /** Returns true when the user holds all of the required permission(s). */
  can: (required: Permission | Permission[]) => boolean;
}

/**
 * usePermissions exposes the current user's permissions, derived role and a
 * `can` helper for conditional rendering and routing decisions.
 */
export default function usePermissions(): UsePermissionsResult {
  const permissions = useSelector(
    (s: RootState) => s.appAuthReducer.user?.permissions,
  );

  return useMemo(() => {
    const perms = permissions ?? [];
    return {
      permissions: perms,
      role: deriveRole(perms),
      can: (required: Permission | Permission[]) =>
        hasPermission(perms, required),
    };
  }, [permissions]);
}
